import type {CreateExtArgs, CreateExtensionArgument, Extension, ExtensionAPI} from '@wix/document-manager-core'
import type {
    Pointer,
    HorizontalMargin,
    MeasureMap,
    MeasureMapWithRows,
    RowsMap,
    SupportedComponentLayout,
    SupportedItemLayout,
    SupportedLayouts,
    VerticalMargin,
    WidthHeightMinMax,
    WidthHeightMinMaxUnset,
    CompRef
} from '@wix/document-services-types'
import {COMP_TYPES, DATA_TYPES} from '../../constants/constants'
import type {VariantsExtensionAPI} from '../variants/variants'
import type {StructureExtensionAPI} from '../structure'
import type {LayoutExtensionApi} from '../layout/layout'
import type {SlotsExtensionAPI} from '../slots'
import {createCalculateMeasurementsAPI} from './calculateMeasurements/calculateMeasurements'
import {ReportableError} from '@wix/document-manager-utils'

const CONTAINER = 'Container'
const DEFAULT_COMPONENT_WIDTH = 300
const SECTION_WIDTH = 1280
const SECTION_HEIGHT = 800
const UNSET = 'unset'
const AUTO = 'auto'

interface MeasurementsAPI extends ExtensionAPI {
    generateMeasurement(pointer: Pointer): MeasureMap
}

interface MeasurementsExtensionAPI extends ExtensionAPI {
    measurements: MeasurementsAPI
}

const createExtension = ({experimentInstance}: CreateExtensionArgument): Extension => {
    const createExtensionAPI = ({dal, pointers, extensionAPI}: CreateExtArgs): MeasurementsExtensionAPI => {
        if (experimentInstance.isOpen('dm_measurementsV2')) {
            const calculateMeasurements = createCalculateMeasurementsAPI({pointers, extensionAPI} as CreateExtArgs)
            return {
                measurements: {
                    generateMeasurement: calculateMeasurements
                }
            }
        }

        const {variants} = extensionAPI as VariantsExtensionAPI
        const {components} = extensionAPI as StructureExtensionAPI
        const {components: layoutComponents} = extensionAPI as LayoutExtensionApi
        const {components: structureComponents} = extensionAPI as StructureExtensionAPI
        const {slots} = extensionAPI as SlotsExtensionAPI
        const getLayoutData = (pointer: Pointer): SupportedLayouts => {
            const layoutPointer = variants.getComponentDataPointerConsideringVariants(pointer, DATA_TYPES.layout)

            if (!layoutPointer) {
                throw new ReportableError({
                    errorType: 'componentWithNoSourceLayout',
                    message: `component ${pointer.id} has no layout to calculate fallback measurements from`,
                    extras: {
                        componentType: structureComponents.getComponentType(pointer)
                    }
                })
            }

            return dal.get(layoutPointer)
        }

        const isSection = (pointer: Pointer): boolean =>
            [COMP_TYPES.RESPONSIVE_SECTION, COMP_TYPES.HEADER_SECTION, COMP_TYPES.FOOTER_SECTION].includes(
                structureComponents.getComponentType(pointer)
            )

        // Helper functions for safe access
        const getParentWidth = (parentId: string, measurementMap: MeasureMap): number => {
            return measurementMap[parentId]?.width ?? SECTION_WIDTH
        }

        const getParentHeight = (parentId: string, measurementMap: MeasureMap): number => {
            return measurementMap[parentId]?.height ?? SECTION_HEIGHT
        }

        const getRowHeight = (parentId: string, rowStart: number, rows: RowsMap): number => {
            return rows[parentId]?.[rowStart] ?? 0
        }

        const calculateMargin = (
            pointer: Pointer,
            measurementsMap: MeasureMap,
            margin: VerticalMargin | HorizontalMargin
        ): number => {
            const parentPointer = pointers.structure.getParent(pointer)!
            switch (margin?.type) {
                case 'px':
                case 'spx':
                    return margin.value
                case 'percentage':
                    return getParentWidth(parentPointer.id, measurementsMap) * margin.value
                default:
                    return 0
            }
        }

        const calculateRowsHeight = (pointer: Pointer, measurementsMap: MeasureMap, rowsMap: RowsMap): void => {
            const childPointers = pointers.structure.getChildren(pointer)
            rowsMap[pointer.id] = childPointers.reduce(
                (rows, childPointer) => {
                    const {itemLayout} = getLayoutData(childPointer)
                    if (!itemLayout) return rows
                    const {gridArea, margins} = itemLayout
                    if (!gridArea) return rows
                    const {rowStart, rowEnd} = gridArea
                    if (rowEnd - rowStart > 1) return rows

                    const {bottom, top} = margins ?? {}
                    rows[rowEnd] =
                        calculateMargin(pointer, measurementsMap, bottom) +
                        calculateMargin(pointer, measurementsMap, top) +
                        measurementsMap[childPointer.id].height

                    return rows
                },
                {1: 0}
            )
        }

        const calculateConsideringMinAndHeight = (
            minMeasure: WidthHeightMinMax | WidthHeightMinMaxUnset,
            maxMeasure: WidthHeightMinMax | WidthHeightMinMaxUnset,
            value: number
        ) => {
            if (minMeasure && minMeasure.type !== UNSET && minMeasure.type !== AUTO)
                value = Math.max(minMeasure.value, value)
            if (maxMeasure && maxMeasure.type !== UNSET && maxMeasure.type !== AUTO)
                value = Math.min(maxMeasure.value, value)
            return value
        }

        const calculateWidth = (
            pointer: Pointer,
            componentLayout: SupportedComponentLayout,
            {measurementMap}: MeasureMapWithRows
        ): number => {
            const {minWidth, maxWidth, width} = componentLayout ?? {}
            let calculatedWidth
            if (isSection(pointer) || isSection(pointers.structure.getParent(pointer)!)) calculatedWidth = SECTION_WIDTH
            else {
                switch (width?.type) {
                    case 'percentage':
                        const parentPointer = pointers.structure.getParent(pointer)!
                        calculatedWidth = (width.value / 100) * getParentWidth(parentPointer.id, measurementMap)
                        break
                    case 'px':
                    case 'spx':
                        calculatedWidth = width.value
                        break
                    default:
                        calculatedWidth = DEFAULT_COMPONENT_WIDTH
                }
            }
            return calculateConsideringMinAndHeight(minWidth, maxWidth, calculatedWidth)
        }

        const calculateHeight = (
            pointer: Pointer,
            componentLayout: SupportedComponentLayout,
            {measurementMap, rows}: MeasureMapWithRows
        ): number => {
            const type = components.getType(pointer)
            const {containerLayout} = getLayoutData(pointer)
            let calculatedHeight
            const parentPointer = pointers.structure.getParent(pointer)!
            if (type === CONTAINER && containerLayout?.type === 'GridContainerLayout') {
                if (isSection(parentPointer) || isSection(pointer)) return SECTION_HEIGHT
                calculateRowsHeight(pointer, measurementMap, rows)
                calculatedHeight = Object.values(rows[pointer.id]).reduce((acc, num) => acc + num, 0)
            } else {
                const {height} = componentLayout
                switch (height?.type) {
                    case 'aspectRatio': {
                        calculatedHeight = height.value * measurementMap[pointer.id].width
                        break
                    }
                    case 'spx':
                    case 'px': {
                        calculatedHeight = height.value
                        break
                    }
                    case 'percentage': {
                        calculatedHeight = (height.value / 100) * getParentHeight(parentPointer.id, measurementMap)
                        break
                    }
                    default:
                        calculatedHeight = 100
                }
            }
            const {minHeight, maxHeight} = componentLayout
            return calculateConsideringMinAndHeight(minHeight, maxHeight, calculatedHeight)
        }

        const calculateX = (
            pointer: Pointer,
            {measurementMap}: MeasureMapWithRows,
            itemLayout: SupportedItemLayout
        ): number => {
            const {margins} = itemLayout
            if (!margins) return 0
            const parentPointer = pointers.structure.getParent(pointer)!
            const parentWidth = getParentWidth(parentPointer.id, measurementMap)

            if (margins.left?.value) {
                switch (margins.left.type) {
                    case 'percentage': {
                        return (margins.left.value / 100) * parentWidth
                    }
                    case 'px':
                    case 'spx':
                        return margins.left.value
                }
            } else if (margins.right?.value) {
                switch (margins.right.type) {
                    case 'percentage': {
                        return (
                            parentWidth - (margins.right.value / 100) * parentWidth - measurementMap[pointer.id].width
                        )
                    }
                    case 'px':
                    case 'spx':
                        return parentWidth - margins.right.value - measurementMap[pointer.id].width
                }
            }
            return 0
        }

        const calculateY = (pointer: Pointer, {measurementMap, rows}: MeasureMapWithRows): number => {
            const parentPointer = pointers.structure.getParent(pointer)!
            const {
                itemLayout: {gridArea, margins, alignSelf}
            } = getLayoutData(pointer)

            switch (alignSelf) {
                case 'center': {
                    return getParentHeight(parentPointer.id, measurementMap) / 2 - measurementMap[pointer.id].height / 2
                }
                case 'start':
                    return (
                        (gridArea ? getRowHeight(parentPointer.id, gridArea.rowStart, rows) : 0) +
                        calculateMargin(pointer, measurementMap, margins?.top)
                    )
                case 'stretch':
                    return 0
                default:
                    return 0
            }
        }

        const calculateLayoutRecursively = (pointer: Pointer, measurementMapWithRows: MeasureMapWithRows): void => {
            const {measurementMap} = measurementMapWithRows
            let layoutData = getLayoutData(pointer)

            if (slots.is.unifiedContainerPlugin(pointer as CompRef)) {
                layoutData = {
                    ...layoutData,
                    componentLayout: {
                        ...layoutData.componentLayout,
                        type: 'ComponentLayout',
                        width: {type: 'percentage', value: 100},
                        height: {type: 'percentage', value: 100}
                    },
                    itemLayout: {
                        id: `${pointer.id}_itemLayout`,
                        type: 'GridItemLayout',
                        alignSelf: 'start',
                        margins: {
                            top: {type: 'px', value: 0},
                            bottom: {type: 'px', value: 0},
                            left: {type: 'px', value: 0},
                            right: {type: 'px', value: 0}
                        },
                        gridArea: {
                            rowStart: 1,
                            rowEnd: 2,
                            columnStart: 1,
                            columnEnd: 2
                        }
                    }
                }
            }

            const {componentLayout, itemLayout} = layoutData
            measurementMap[pointer.id] = measurementMap[pointer.id] ?? {}
            measurementMap[pointer.id].width = calculateWidth(pointer, componentLayout, measurementMapWithRows)
            measurementMap[pointer.id].x = calculateX(pointer, measurementMapWithRows, itemLayout)

            pointers.structure.getChildren(pointer).forEach(childPointer => {
                calculateLayoutRecursively(childPointer, measurementMapWithRows)
            })

            measurementMap[pointer.id].height = calculateHeight(pointer, componentLayout, measurementMapWithRows)
            pointers.structure.getChildren(pointer).forEach(childPointer => {
                measurementMap[childPointer.id].y = calculateY(childPointer, measurementMapWithRows)
            })
            if (!measurementMap[pointer.id].y) measurementMap[pointer.id].y = 0
        }

        const generateMeasurement = (pointer: Pointer): MeasureMap => {
            const measurementMapWithRows: MeasureMapWithRows = {measurementMap: {}, rows: {}}
            if (
                !isSection(pointer) &&
                !(
                    layoutComponents.isPinned(pointer) &&
                    pointers.structure.getParent(pointer) &&
                    pointers.structure.isPage(pointers.structure.getParent(pointer)!)
                )
            )
                throw new Error(
                    `pointer ${pointer} is not a section nor a pinned to page element , cannot generate fallback measurement`
                )
            calculateLayoutRecursively(pointer, measurementMapWithRows)
            const {measurementMap} = measurementMapWithRows
            return measurementMap
        }

        return {
            measurements: {
                generateMeasurement
            }
        }
    }

    return {
        name: 'measurements',
        createExtensionAPI
    }
}

export {createExtension, MeasurementsExtensionAPI}
